import { z } from "zod";

export const ParticipantActivityType = {
  CREATED: "participant.created",
  INVITED: "participant.invited",
  CONTACTED: "participant.contacted",
  CONFIRMED: "participant.confirmed",
  JOINED: "participant.joined",
  REMOVED: "participant.removed",
  RESTORED: "participant.restored",
  ANONYMIZED: "participant.anonymized",
  UPDATED: "participant.updated",
  CONTACT_UPDATED: "participant.contact_updated",
  VIP_UPDATED: "participant.vip_updated",

  USER_LINKED: "participant.user_linked",

  OPT_IN_REMEMBER: "participant.opt_in_remember",
} as const;

export const PersonalInvitationActivityType = {
  CREATED: "invitation.created",
  SENT: "invitation.sent",

  INTERACTED: "invitation.interacted",
  EXPIRED: "invitation.expired",

  JOINED: "invitation.joined",

  MAGIC_LINK_ACCESSED: "invitation.magic_link_accessed",
} as const;

export const OpenInvitationActivityType = {
  CREATED: "open_invitation.created",

  DISABLED: "open_invitation.disabled",

  EXHAUSTED: "open_invitation.exhausted",

  EXPIRED: "open_invitation.expired",

  JOINED: "open_invitation.joined",
} as const;

export const ReminderActivityType = {
  SCHEDULED: "reminder.scheduled",
  TRIGGERED: "reminder.triggered",
  CANCELLED: "reminder.cancelled",
} as const;

export const EventActivityType = {
  CREATED: "event.created",
  UPDATED: "event.updated",
  PUBLISHED: "event.published",
  STARTED: "event.started",
  ENDED: "event.ended",
  CANCELLED: "event.cancelled",
  HOST_ADDED: "event.host_added",
  HOST_REMOVED: "event.host_removed",
} as const;

export const MagicLinkActivityTypes = {
  CREATED: "magic-link.created",

  VALIDATED: "magic-link.validated",

  VALIDATED_FAILED: "magic-link.validated_failed",

  CONSUMED: "magic-link.consumed",

  EXPIRED: "magic-link.expired",
} as const;

export const NotificationActivityType = {
  CREATED: "notification.created",

  SENT: "notification.sent",

  FAILED: "notification.failed",

  CHANNEL_ATTEMPT: "notification.channel_attempt",
  /** Notification rejected (e.g., recipient has no valid channels) */
  REJECTED: "notification.rejected",

  SCHEDULED: "notification.scheduled",

  CANCELLED: "notification.cancelled",
} as const;

/**
 * FeatureConfigActivityType as a plain const object for the central
 * `ActivityActionType` registry and the `ActivityActionTypeSchema` z.enum.
 *
 * Uses string literals directly so the inferred value types are literal unions
 * rather than an opaque TS `enum` member type — otherwise downstream `z.enum`
 * consumers see a type that isn't structurally assignable from literal strings.
 */
export const FeatureConfigActivityTypeObject = {
  CREATED: "feature_config.created",
  UPDATED: "feature_config.updated",
  DELETED: "feature_config.deleted",
} as const;

export const FeatureConfigActivityTypeSchema = z.enum(
  FeatureConfigActivityTypeObject,
);

export type FeatureConfigActivityType = z.infer<
  typeof FeatureConfigActivityTypeSchema
>;

export const FeatureConfigActivityType = {
  ...FeatureConfigActivityTypeObject,
} as const;

export const ScheduledTaskActivityType = {
  CREATED: "scheduled_task.created",

  STARTED: "scheduled_task.started",

  SUCCEEDED: "scheduled_task.succeeded",

  FAILED: "scheduled_task.failed",

  CANCELLED: "scheduled_task.cancelled",

  RETRY_SCHEDULED: "scheduled_task.retry_scheduled",
} as const;

export const CustomFieldDefinitionActivityType = {
  CREATED: "custom_field_definition.created",

  UPDATED: "custom_field_definition.updated",

  ARCHIVED: "custom_field_definition.archived",
} as const;

export const UserActivityType = {
  CREATED: "user.created",

  UPDATED: "user.updated",
} as const;

export const UserChannelActivityType = {
  CHANNEL_ADDED: "user.channel_added",

  CHANNEL_REMOVED: "user.channel_removed",

  CHANNEL_VERIFIED: "user.channel_verified",

  CHANNEL_VERIFICATION_FAILED: "user.channel_verification_failed",
} as const;

export const ImportActivityType = {
  IMPORT_STARTED: "participant.import_started",

  IMPORTED: "participant.imported",

  IMPORT_COMPLETED: "participant.import_completed",

  IMPORT_FAILED: "participant.import_failed",
} as const;

/**
 * Suggestion Generation activity types
 *
 * Activity event action types for suggestion generation operations.
 * Follows naming convention: `{domain}.{action}` (Rule activity-domain-action-naming)
 *
 * Note: String values kept as "event_grouping.*" for backward compatibility with existing DB records.
 */
export const SuggestionGenerationActivityType = {
  RUN_STARTED: "event_grouping.run_started",

  RUN_COMPLETED: "event_grouping.run_completed",

  RUN_FAILED: "event_grouping.run_failed",
  /** Suggestion generation run cancelled by host/admin */
  RUN_CANCELLED: "event_grouping.run_cancelled",
} as const;

/**
 * Activity event action types for the suggestions aggregate (authoring, batch creation, queries).
 */
export const SuggestionActivityType = {
  SUGGESTION_CREATED: "reason.created",
  SUGGESTIONS_BATCH_CREATED: "reason.batch_created",
  SUGGESTION_VIEWED: "reason.viewed",
  SUGGESTIONS_QUERIED_BY_RUN: "reason.queried_by_run",
} as const;

export const DirectoryActivityType = {
  ENTRY_VIEWED: "directory.entry_viewed",

  SEARCH_PERFORMED: "directory.search_performed",

  CONTACT_INFO_ACCESSED: "directory.contact_info_accessed",

  EXPORT_PERFORMED: "directory.export_performed",
} as const;

export const DeepLinkAuthActivityType = {
  TOKEN_CREATED: "deep_link_auth.token_created",

  TOKEN_VALIDATED: "deep_link_auth.token_validated",

  TOKEN_REVOKED: "deep_link_auth.token_revoked",

  TOKEN_EXPIRED: "deep_link_auth.token_expired",
} as const;

export const ProfilesActivityType = {
  USER_PROFILE_CREATED: "profiles.user_profile_created",

  USER_PROFILE_UPDATED: "profiles.user_profile_updated",

  HOST_PROFILE_UPDATED: "profiles.host_profile_updated",

  VISIBILITY_CHANGED: "profiles.visibility_changed",
} as const;

export const EnrichmentActivityType = {
  REQUEST_CREATED: "enrichment.request_created",

  REQUEST_PROCESSING_STARTED: "enrichment.request_processing_started",

  REQUEST_COMPLETED: "enrichment.request_completed",

  REQUEST_FAILED: "enrichment.request_failed",

  REQUEST_RETRIED: "enrichment.request_retried",

  DATA_UPDATED: "enrichment.data_updated",
} as const;

export type EnrichmentActivityTypeType =
  (typeof EnrichmentActivityType)[keyof typeof EnrichmentActivityType];

export const IntroActivityType = {
  REQUESTED: "intro.requested",

  SENT: "intro.sent",

  SEEN: "intro.seen",

  ACCEPTED: "intro.accepted",

  DECLINED: "intro.declined",

  CANCELLED: "intro.cancelled",
  /** Both requests auto-accepted due to mutual interest detection (Stage-4C) */
  MUTUAL_INTEREST_AUTO_ACCEPTED: "intro.mutual_interest_auto_accepted",

  NOTIFICATION_SENT: "intro.notification_sent",

  NOTIFICATION_SKIPPED: "intro.notification_skipped",

  MUTUAL_INTEREST_NOTIFICATION_SENT: "intro.mutual_interest_notification_sent",

  ACCEPTED_NOTIFICATION_SENT: "intro.accepted_notification_sent",

  DECLINED_NOTIFICATION_SENT: "intro.declined_notification_sent",
} as const;

export const RelationshipActivityType = {
  CREATED: "relationship.created",

  UPDATED: "relationship.updated",

  ARCHIVED: "relationship.archived",

  INTERACTION_RECORDED: "relationship.interaction_recorded",
} as const;

export const OrganizationsActivityType = {
  CREATED: "organizations.created",

  UPDATED: "organizations.updated",

  SUSPENDED: "organizations.suspended",

  REACTIVATED: "organizations.reactivated",

  ARCHIVED: "organizations.archived",

  MEMBER_ADDED: "organizations.member_added",

  MEMBER_REMOVED: "organizations.member_removed",

  MEMBER_ROLE_CHANGED: "organizations.member_role_changed",
} as const;

export const AuthenticationActivityType = {
  SESSION_CREATED: "authentication.session_created",

  SESSION_REFRESHED: "authentication.session_refreshed",

  SESSION_REVOKED: "authentication.session_revoked",

  SESSION_EXPIRED: "authentication.session_expired",

  OAUTH_CONNECTED: "authentication.oauth_connected",

  OAUTH_DISCONNECTED: "authentication.oauth_disconnected",

  ALL_SESSIONS_REVOKED: "authentication.all_sessions_revoked",
} as const;

export const CommunicationsActivityType = {
  TEXT_MESSAGE_SENT: "communications.text_message_sent",

  MESSAGE_DELIVERED: "communications.message_delivered",

  MESSAGE_READ: "communications.message_read",

  MESSAGE_FAILED: "communications.message_failed",

  TEMPLATE_SENT: "communications.template_sent",

  INTERACTIVE_SENT: "communications.interactive_sent",

  QUICK_REPLY_SENT: "communications.quick_reply_sent",

  MESSAGE_RECEIVED: "communications.message_received",

  BUTTON_RESPONSE_RECEIVED: "communications.button_response_received",

  QUICK_REPLY_RESPONSE_RECEIVED: "communications.quick_reply_response_received",
} as const;

export const PorterActivityType = {
  RESPONSE_SENT: "porter.response_sent",

  RESPONSE_FAILED: "porter.response_failed",

  PAST_EVENT_ACKNOWLEDGED: "porter.past_event_acknowledged",

  CONTEXT_INVALIDATED: "porter.context_invalidated",

  CONVERSATION_RESUMED: "porter.conversation_resumed",

  INTENT_RESOLVED: "porter.intent_resolved",

  INTENT_UNRESOLVED: "porter.intent_unresolved",

  DIRECTORY_SEARCH_PERFORMED: "porter.directory_search_performed",
} as const;

export const AIModelActivityType = {
  REQUEST_SUBMITTED: "ai_model.request_submitted",

  REQUEST_STARTED: "ai_model.request_started",

  REQUEST_COMPLETED: "ai_model.request_completed",

  REQUEST_FAILED: "ai_model.request_failed",

  PROVIDER_ATTEMPT: "ai_model.provider_attempt",

  VALIDATION_FAILED: "ai_model.validation_failed",
  /** AIModel fallback to next provider/model */
  FALLBACK_TRIGGERED: "ai_model.fallback_triggered",

  RATE_LIMIT_HIT: "ai_model.rate_limit_hit",
} as const;

export const QueueActivityType = {
  ENTRY_ENQUEUED: "queue.entry.enqueued",

  BATCH_ENQUEUED: "queue.batch.enqueued",

  PROCESSING_STARTED: "queue.entry.processing_started",

  PROCESSING_SUCCEEDED: "queue.entry.processing_succeeded",

  PROCESSING_FAILED: "queue.entry.processing_failed",

  AWAITING_CALLBACK: "queue.entry.awaiting_callback",

  CALLBACK_RECEIVED: "queue.entry.callback_received",

  CALLBACK_PROCESSED: "queue.entry.callback_processed",

  CALLBACK_TIMED_OUT: "queue.entry.callback_timed_out",
  /** Stale callback was rejected */
  CALLBACK_STALE_REJECTED: "queue.callback.stale_rejected",

  RETRY_SCHEDULED: "queue.entry.retry_scheduled",

  ENTRY_CLAIMED: "queue.entry.claimed",

  ENTRY_RELEASED: "queue.entry.released",
} as const;

export const ConnectionRequestAdminActivityType = {
  SINGLE_RESET: "connection_request_admin.single_reset",

  PARTICIPANT_RESET: "connection_request_admin.participant_reset",

  EVENT_RESET: "connection_request_admin.event_reset",
} as const;

export const ActivityActionType = {
  Participant: ParticipantActivityType,
  Invitation: PersonalInvitationActivityType,
  OpenInvitation: OpenInvitationActivityType,
  Reminder: ReminderActivityType,
  Event: EventActivityType,
  MagicLink: MagicLinkActivityTypes,
  Notification: NotificationActivityType,
  FeatureConfig: FeatureConfigActivityTypeObject,
  ScheduledTask: ScheduledTaskActivityType,
  CustomFieldDefinition: CustomFieldDefinitionActivityType,
  User: UserActivityType,
  UserChannel: UserChannelActivityType,
  Import: ImportActivityType,
  Suggestion: SuggestionActivityType,
  SuggestionGeneration: SuggestionGenerationActivityType,
  Directory: DirectoryActivityType,
  DeepLinkAuth: DeepLinkAuthActivityType,
  Profiles: ProfilesActivityType,
  Enrichment: EnrichmentActivityType,
  Intro: IntroActivityType,
  Relationship: RelationshipActivityType,
  Organizations: OrganizationsActivityType,
  Authentication: AuthenticationActivityType,
  Communications: CommunicationsActivityType,
  Porter: PorterActivityType,
  AIModel: AIModelActivityType,
  Queue: QueueActivityType,
  ConnectionRequestAdmin: ConnectionRequestAdminActivityType,
} as const;

type ActivityModuleValuesType<T> =
  T extends Record<string, infer V> ? V : never;
type AllActivityModulesType =
  (typeof ActivityActionType)[keyof typeof ActivityActionType];
export type ActivityActionTypeType =
  ActivityModuleValuesType<AllActivityModulesType>;

/**
 * Every registered activity action value, flattened from the per-module
 * `ActivityActionType` registry. Adding a new value to any module extends
 * this list (and therefore the Zod schema) automatically.
 *
 * Uses an array rather than an object spread because module constants share
 * keys like `CREATED` / `UPDATED` across domains — an object spread would
 * silently drop values on key collision.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Object.values widens the `as const` registry values back to `string[]`; reassert the narrow union + non-empty tuple shape that z.enum requires (both facts hold statically).
const ACTIVITY_ACTION_TYPE_VALUES = Object.values(ActivityActionType).flatMap(
  (module) => Object.values(module),
) as [ActivityActionTypeType, ...ActivityActionTypeType[]];

export const ActivityActionTypeSchema = z.enum(ACTIVITY_ACTION_TYPE_VALUES);
